/* eslint-disable max-lines-per-function */
import { useSuspenseQuery } from '@apollo/client/react';
import {
  Absatz,
  AbsatzPlan,
  AbsatzProduktivitaet,
  Currency,
  JahrMonat,
  Money,
  Umsatz,
  UmsatzPlan,
} from '../entities';
import { GET_PLANWERTE_BY_STANDORT_ID } from './graphql/queries';
import * as Sentry from '@sentry/react';
import { Planwert } from '../entities/planwert';
import {
  GetPlanwerteByStandortIdQuery,
  GetPlanwerteByStandortIdQueryVariables,
} from '@/gql/graphql';

export function usePlanwerte({
  jahrMonat,
  standortId,
}: {
  jahrMonat: JahrMonat;
  standortId: string;
}): {
  planwerte: Planwert[];
  error?: string;
} {
  const { data, error } = useSuspenseQuery<
    GetPlanwerteByStandortIdQuery,
    GetPlanwerteByStandortIdQueryVariables
  >(GET_PLANWERTE_BY_STANDORT_ID, {
    variables: {
      jahr: jahrMonat.jahr,
      monat: jahrMonat.monat,
      standortId,
    },
    fetchPolicy: 'network-only',
  });

  if (error) {
    Sentry.captureException(error);
    return { error: error?.message, planwerte: [] };
  }

  const mappedPlanwerte = data.controlling[0].planwerte.map(
    (currentPlanwert) => {
      const currentAbsatzPlanwert = currentPlanwert.absatzPlan.plan;
      const mappedAbsatz = new Absatz({
        produktivitaet: currentAbsatzPlanwert.produktivitaet
          ? new AbsatzProduktivitaet({
              stueck: currentAbsatzPlanwert.produktivitaet.stueck ?? undefined,
              mpt: currentAbsatzPlanwert.produktivitaet.mpt ?? undefined,
            })
          : new AbsatzProduktivitaet({}),
        wert: currentAbsatzPlanwert.wert ?? undefined,
        stunden: currentAbsatzPlanwert.stunden ?? undefined,
      });
      const mappedAbsatzPlanwert = new AbsatzPlan({
        absatz: mappedAbsatz,
        prognose: currentPlanwert.absatzPlan.prognose ?? null,
      });

      const currentUmsatzPlanwert = currentPlanwert.umsatzPlan.plan;
      const mappedUmsatz = new Umsatz({
        produktivitaet: currentUmsatzPlanwert.produktivitaet
          ? new Money({
              amount: currentUmsatzPlanwert.produktivitaet.amount,
              currency: Currency[currentUmsatzPlanwert.produktivitaet.currency],
            })
          : undefined,
        wert: currentUmsatzPlanwert.wert
          ? new Money({
              amount: currentUmsatzPlanwert.wert.amount,
              currency: Currency[currentUmsatzPlanwert.wert.currency],
            })
          : undefined,
        stunden: currentUmsatzPlanwert.stunden ?? undefined,
      });

      const mappedUmsatzPlanwert = new UmsatzPlan({
        umsatz: mappedUmsatz,
        prognose: currentPlanwert.umsatzPlan.prognose
          ? new Money({
              amount: currentPlanwert.umsatzPlan.prognose.amount,
              currency: Currency[currentPlanwert.umsatzPlan.prognose.currency],
            })
          : null,
      });

      const newPlanwert = new Planwert({
        datum: currentPlanwert.datum,
        interneNotiz: currentPlanwert.interneNotiz,
        absatzPlan: mappedAbsatzPlanwert,
        umsatzPlan: mappedUmsatzPlanwert,
      });
      return newPlanwert;
    },
  );

  return { planwerte: mappedPlanwerte };
}
